import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { LanguageProvider } from "@/i18n";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import Layout from "@/components/Layout";
import Onboarding from "@/pages/Onboarding";
import Home from "@/pages/Home";
import Properties from "@/pages/Properties";
import ListingWizard from "@/pages/ListingWizard";
import PropertyDetail from "@/pages/PropertyDetail";
import Payments from "@/pages/Payments";
import Profile from "@/pages/Profile";
import Agreements from "@/pages/Agreements";
import AgreementCreate from "@/pages/AgreementCreate";
import AgreementDetail from "@/pages/AgreementDetail";
import Maintenance from "@/pages/Maintenance";
import ConditionReport from "@/pages/ConditionReport";
import ResetPassword from "@/pages/ResetPassword";
import AdminDashboard from "@/pages/AdminDashboard";
import { Toaster } from "@/components/ui/sonner";

function Splash() {
  return <div className="min-h-screen flex items-center justify-center bg-sand"><Loader2 className="w-7 h-7 animate-spin text-terracotta" /></div>;
}

function Protected({ children, bare }) {
  const { user } = useAuth();
  if (user === null) return <Splash />;
  if (user === false) return <Navigate to="/" replace />;
  return bare ? children : <Layout>{children}</Layout>;
}

function Gate() {
  const { user } = useAuth();
  if (user === null) return <Splash />;
  if (user) return <Navigate to={user.role === "admin" ? "/admin" : "/home"} replace />;
  return <Onboarding />;
}

function RouteTracker() {
  const location = useLocation();
  useEffect(() => {
    api.post("/track/visit", { path: location.pathname, referrer: document.referrer }).catch(() => {});
  }, [location.pathname]);
  return null;
}

function App() {
  return (
    <div className="App">
      <Toaster richColors position="top-center" />
      <LanguageProvider>
        <BrowserRouter>
          <AuthProvider>
            <RouteTracker />
            <Routes>
              <Route path="/" element={<Gate />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/admin" element={<Protected bare><AdminDashboard /></Protected>} />
              <Route path="/home" element={<Protected><Home /></Protected>} />
              <Route path="/properties" element={<Protected><Properties /></Protected>} />
              <Route path="/properties/new" element={<Protected bare><ListingWizard /></Protected>} />
              <Route path="/properties/:id" element={<Protected bare><PropertyDetail /></Protected>} />
              <Route path="/payments" element={<Protected><Payments /></Protected>} />
              <Route path="/profile" element={<Protected><Profile /></Protected>} />
              <Route path="/agreements" element={<Protected><Agreements /></Protected>} />
              <Route path="/agreements/new" element={<Protected bare><AgreementCreate /></Protected>} />
              <Route path="/agreements/:id" element={<Protected bare><AgreementDetail /></Protected>} />
              <Route path="/maintenance" element={<Protected><Maintenance /></Protected>} />
              <Route path="/condition-report" element={<Protected bare><ConditionReport /></Protected>} />
              <Route path="*" element={<Navigate to="/home" replace />} />
            </Routes>
          </AuthProvider>
        </BrowserRouter>
      </LanguageProvider>
    </div>
  );
}

export default App;
